import { useReducer, useEffect, useRef, type ReactNode } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { GetPickingResponse, ScanRequest, ScannedBagInput } from "@pm-barcode/shared";
import { useApiClient } from "../../hooks/use-api-client.ts";
import { useScanner } from "../../hooks/use-scanner.ts";
import { RequestError } from "../../api/client.ts";
import { useSettings } from "../../context/settings-context.tsx";

// ─── Audio / Haptic ─────────────────────────────────────────────────────────

function vibrate(pattern: number | number[]): void {
  if (!("vibrate" in navigator)) return;
  navigator.vibrate(pattern);
}

let audioCtx: AudioContext | null = null;

function beep(type: "success" | "error"): void {
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    const play = () => {
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.connect(gain);
      gain.connect(audioCtx!.destination);
      osc.frequency.value = type === "success" ? 880 : 220;
      const now = audioCtx!.currentTime;
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    };
    // WebView AudioContext may start suspended when triggered from native code.
    if (audioCtx.state === "suspended") {
      void audioCtx.resume().then(play);
    } else {
      play();
    }
  } catch {
    // Ignore if audio is unavailable
  }
}

// ─── State machine ──────────────────────────────────────────────────────────

type Feedback =
  | { kind: "ok"; productName: string; weightKg: number; batch: string; bagNumber?: string }
  | { kind: "err"; title: string };

type Modal =
  | { kind: "unknown"; rawBarcode: string }
  | { kind: "not_on_picking"; category: string; batch: string }
  | { kind: "already_scanned"; lotName: string }
  | { kind: "already_received"; lotName: string }
  | { kind: "variance"; expected: number; actual: number }
  | { kind: "error"; title: string; message: string };

interface State {
  phase: "loading" | "load_error" | "ready" | "validating" | "done";
  picking: GetPickingResponse | null;
  errorMsg: string | null;
  localQty: Record<number, number>;
  /** Bags scanned this session — passed to the validate endpoint for deferred commit. */
  scannedBags: ScannedBagInput[];
  feedback: Feedback | null;
  modal: Modal | null;
  scanSeq: number;
  backorder: { created: boolean; ref?: string } | null;
}

type Action =
  | { type: "LOADED"; picking: GetPickingResponse }
  | { type: "LOAD_FAIL"; msg: string }
  | { type: "SCAN_OK"; productId: number; productName: string; qtyDone: number; weightKg: number; batch: string; bagNumber?: string; bag: ScannedBagInput }
  | { type: "SCAN_UNKNOWN"; rawBarcode: string }
  | { type: "SCAN_NOT_ON_PICKING"; category: string; batch: string }
  | { type: "SCAN_ALREADY_SCANNED"; lotName: string }
  | { type: "SCAN_ALREADY_RECEIVED"; lotName: string }
  | { type: "SCAN_VARIANCE"; expected: number; actual: number; bag: ScannedBagInput }
  | { type: "SCAN_ERR"; title: string; msg: string }
  | { type: "DISMISS" }
  | { type: "VALIDATE_START" }
  | { type: "VALIDATE_OK"; backorderCreated: boolean; backorderRef?: string }
  | { type: "VALIDATE_FAIL"; msg: string };

const initialState: State = {
  phase: "loading",
  picking: null,
  errorMsg: null,
  localQty: {},
  scannedBags: [],
  feedback: null,
  modal: null,
  scanSeq: 0,
  backorder: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "LOADED":
      return { ...state, phase: "ready", picking: action.picking };
    case "LOAD_FAIL":
      return { ...state, phase: "load_error", errorMsg: action.msg };
    case "SCAN_OK":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: {
          kind: "ok",
          productName: action.productName,
          weightKg: action.weightKg,
          batch: action.batch,
          bagNumber: action.bagNumber,
        },
        localQty: {
          ...state.localQty,
          [action.productId]: (state.localQty[action.productId] ?? 0) + action.qtyDone,
        },
        scannedBags: [...state.scannedBags, action.bag],
        modal: null,
      };
    case "SCAN_VARIANCE":
      // Over-dispatch bag: warn but still track for commit at validate time.
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Extra bag" },
        localQty: {
          ...state.localQty,
          [action.bag.productId]:
            (state.localQty[action.bag.productId] ?? 0) +
            (action.bag.tracking === "serial" ? 1 : action.bag.quantity),
        },
        scannedBags: [...state.scannedBags, action.bag],
        modal: { kind: "variance", expected: action.expected, actual: action.actual },
      };
    case "SCAN_UNKNOWN":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Unknown barcode" },
        modal: { kind: "unknown", rawBarcode: action.rawBarcode },
      };
    case "SCAN_NOT_ON_PICKING":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Not on picking" },
        modal: { kind: "not_on_picking", category: action.category, batch: action.batch },
      };
    case "SCAN_ALREADY_SCANNED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Already scanned" },
        modal: { kind: "already_scanned", lotName: action.lotName },
      };
    case "SCAN_ALREADY_RECEIVED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Already dispatched" },
        modal: { kind: "already_received", lotName: action.lotName },
      };
    case "SCAN_ERR":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: action.title },
        modal: { kind: "error", title: action.title, message: action.msg },
      };
    case "DISMISS":
      return { ...state, modal: null };
    case "VALIDATE_START":
      return { ...state, phase: "validating", errorMsg: null };
    case "VALIDATE_OK":
      return {
        ...state,
        phase: "done",
        backorder: { created: action.backorderCreated, ref: action.backorderRef },
      };
    case "VALIDATE_FAIL":
      return { ...state, phase: "ready", errorMsg: action.msg };
    default:
      return state;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DispatchPicking() {
  const navigate = useNavigate();
  const api = useApiClient();
  const { hapticEnabled, setHapticEnabled } = useSettings();
  const [state, dispatch] = useReducer(reducer, initialState);
  const scanInFlight = useRef(false);
  // Ref-based dedup: updated synchronously on accept, no closure-timing gap.
  const scannedSerialIdsRef = useRef<Set<number>>(new Set());

  const { ref } = useParams({ strict: false }) as { ref: string };

  useEffect(() => {
    if (!api || !ref) return;
    let cancelled = false;
    api
      .getPicking(ref)
      .then((picking) => {
        if (!cancelled) dispatch({ type: "LOADED", picking });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          dispatch({
            type: "LOAD_FAIL",
            msg: err instanceof Error ? err.message : "Failed to load picking",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, ref]);

  useScanner((event) => {
    if (state.phase !== "ready" || !api || scanInFlight.current) return;
    scanInFlight.current = true;

    const body: ScanRequest = {
      rawBarcode: event.rawBarcode,
      symbology: event.symbology,
      workflow: "dispatch",
      pickingRef: ref,
      deviceId: import.meta.env.VITE_DEVICE_ID,
      capturedAt: event.capturedAt,
    };

    void api
      .postScan(body)
      .then((result) => {
        if (result.outcome === "ok") {
          const bag: ScannedBagInput = {
            lotId: result.odoo.lotId,
            lotName: result.odoo.lotName,
            quantity: result.normalised.weightKg,
            moveId: result.odoo.moveId,
            sourceLocationId: result.odoo.sourceLocationId,
            destLocationId: result.odoo.destLocationId,
            productId: result.odoo.productId,
            tracking: result.odoo.tracking,
          };

          // Duplicate detection for serial products — ref avoids any closure-timing gap.
          if (result.odoo.tracking === "serial" && scannedSerialIdsRef.current.has(result.odoo.lotId)) {
            beep("error");
            if (hapticEnabled) vibrate([100, 50, 100]);
            dispatch({ type: "SCAN_ALREADY_SCANNED", lotName: result.odoo.lotName });
            return;
          }

          // Client-side over-dispatch check.
          const move = state.picking?.moves.find((m) => m.product.id === result.odoo.productId);
          const currentQty = state.localQty[result.odoo.productId] ?? 0;
          const newQty = currentQty + result.odoo.qtyDone;
          if (move && newQty > move.expectedQty) {
            beep("error");
            if (hapticEnabled) vibrate([100, 50, 100]);
            if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
            dispatch({ type: "SCAN_VARIANCE", expected: move.expectedQty, actual: newQty, bag });
            return;
          }

          if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
          beep("success");
          if (hapticEnabled) vibrate(200);
          dispatch({
            type: "SCAN_OK",
            productId: result.odoo.productId,
            productName: result.odoo.productName,
            qtyDone: result.odoo.qtyDone,
            weightKg: result.normalised.weightKg,
            batch: result.normalised.batch,
            bagNumber: result.normalised.bagNumber,
            bag,
          });
        } else if (result.outcome === "unknown") {
          beep("error");
          if (hapticEnabled) vibrate([100, 50, 100]);
          dispatch({ type: "SCAN_UNKNOWN", rawBarcode: result.rawBarcode });
        } else if (result.outcome === "not_on_picking") {
          beep("error");
          if (hapticEnabled) vibrate([100, 50, 100]);
          dispatch({
            type: "SCAN_NOT_ON_PICKING",
            category: result.normalised.category,
            batch: result.normalised.batch,
          });
        }
      })
      .catch((err: unknown) => {
        beep("error");
        if (hapticEnabled) vibrate([100, 50, 100]);
        if (err instanceof RequestError && err.code === "duplicate_serial") {
          dispatch({ type: "SCAN_ALREADY_RECEIVED", lotName: err.message.match(/Bag (.+?) has/)?.[1] ?? "this bag" });
        } else {
          dispatch({
            type: "SCAN_ERR",
            title: "Scan failed",
            msg: err instanceof Error ? err.message : "Unknown error",
          });
        }
      })
      .finally(() => {
        scanInFlight.current = false;
      });
  });

  async function handleValidate() {
    if (!api || state.phase !== "ready") return;
    dispatch({ type: "VALIDATE_START" });
    try {
      const result = await api.validatePicking(ref, { acceptBackorder: true, scans: state.scannedBags });
      dispatch({
        type: "VALIDATE_OK",
        backorderCreated: result.backorderCreated,
        backorderRef: result.backorderPickingRef,
      });
    } catch (err) {
      dispatch({
        type: "VALIDATE_FAIL",
        msg: err instanceof Error ? err.message : "Validation failed",
      });
    }
  }



  // ── Loading ──
  if (state.phase === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-gray-400">Loading picking…</p>
      </div>
    );
  }

  // ── Load error ──
  if (state.phase === "load_error") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-red-400">Failed to load picking</p>
        <p className="text-sm text-gray-400">{state.errorMsg}</p>
        <button
          onClick={() => void navigate({ to: "/dispatch" })}
          className="rounded-xl bg-gray-800 px-6 py-3 font-semibold active:bg-gray-700"
        >
          Back
        </button>
      </div>
    );
  }

  // ── Done ──
  if (state.phase === "done") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-6 text-center">
        <div className="text-6xl text-green-400">✓</div>
        <div>
          <p className="text-xl font-bold">Dispatch validated</p>
          <p className="mt-1 font-mono text-gray-300">{ref}</p>
        </div>
        {state.backorder?.created && state.backorder.ref && (
          <div className="rounded-xl bg-yellow-900/40 px-4 py-3 text-sm text-yellow-300">
            Backorder created: {state.backorder.ref}
          </div>
        )}
        <button
          onClick={() => void navigate({ to: "/workflow" })}
          className="w-full max-w-xs rounded-xl bg-green-600 py-4 text-lg font-semibold active:bg-green-700"
        >
          Back to workflow
        </button>
      </div>
    );
  }

  // ── Ready / Validating ──
  const picking = state.picking!;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3">
        <button
          onClick={() => void navigate({ to: "/dispatch" })}
          className="text-sm text-gray-400 active:text-white"
        >
          ←
        </button>
        <div className="flex-1">
          <p className="font-mono text-sm font-semibold">{picking.picking.name}</p>
          <p className="text-xs capitalize text-gray-500">{picking.picking.state}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              const next = !hapticEnabled;
              setHapticEnabled(next);
              if (next) vibrate(100);
            }}
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${hapticEnabled ? "bg-green-600 text-white" : "bg-gray-800 text-gray-500"}`}
          >
            Buzz
          </button>
          <span className="rounded-full bg-green-900/50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-green-300">
            Dispatch
          </span>
        </div>
      </div>

      {/* Picking error (validate failed) */}
      {state.errorMsg && (
        <div className="mx-4 mt-3 rounded-xl bg-red-900/40 px-4 py-3 text-sm text-red-300">
          <p className="font-semibold">Validation failed — tap Retry to try again.</p>
          <p className="mt-1 text-xs text-red-400">{state.errorMsg}</p>
        </div>
      )}

      {/* Progress bar */}
      {(() => {
        const totalExpected = picking.moves.reduce((s, m) => s + m.expectedQty, 0);
        const totalDone = picking.moves.reduce((s, m) => s + m.doneQty + (state.localQty[m.product.id] ?? 0), 0);
        const pct = totalExpected > 0 ? Math.min(100, (totalDone / totalExpected) * 100) : 0;
        const complete = totalDone >= totalExpected;
        return (
          <div className="px-4 pb-2 pt-3">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="text-gray-500">Progress</span>
              <span className={`font-semibold tabular-nums ${complete ? "text-green-400" : "text-white"}`}>
                {totalDone} / {totalExpected} bags
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-800">
              <div
                className={`h-full rounded-full transition-all duration-300 ${complete ? "bg-green-500" : "bg-green-600"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })()}

      {/* Move lines table */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-900">
            <tr className="border-b border-gray-800 text-xs uppercase tracking-widest text-gray-500">
              <th className="py-2 pl-4 text-left font-normal">Product</th>
              <th className="py-2 pr-3 text-right font-normal">Plan</th>
              <th className="py-2 pr-4 text-right font-normal">Picked</th>
            </tr>
          </thead>
          <tbody>
            {picking.moves.map((move) => {
              const done = move.doneQty + (state.localQty[move.product.id] ?? 0);
              const complete = done >= move.expectedQty;
              return (
                <tr key={move.id} className="border-b border-gray-800/60">
                  <td className="py-3 pl-4 text-sm leading-tight">{move.product.name}</td>
                  <td className="py-3 pr-3 text-right tabular-nums text-gray-400">
                    {move.expectedQty}
                  </td>
                  <td
                    className={`py-3 pr-4 text-right font-semibold tabular-nums ${
                      complete ? "text-green-400" : "text-white"
                    }`}
                  >
                    {done}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Scan feedback panel */}
      <div className="border-t border-gray-800 p-4">
        <div
          key={state.scanSeq}
          className={`rounded-2xl p-4 text-center transition-colors ${
            state.feedback?.kind === "ok"
              ? "bg-scan-ok/20 animate-flash"
              : state.feedback?.kind === "err"
                ? "bg-scan-error/20 animate-flash"
                : "bg-gray-900"
          }`}
        >
          {state.feedback?.kind === "ok" ? (
            <>
              <p className="text-2xl font-bold text-scan-ok">
                ✓ {state.feedback.weightKg} kg
              </p>
              <p className="mt-0.5 text-sm text-gray-300">{state.feedback.productName}</p>
              <p className="text-xs text-gray-500">
                Batch {state.feedback.batch}
                {state.feedback.bagNumber ? ` · Bag ${state.feedback.bagNumber}` : ""}
              </p>
            </>
          ) : state.feedback?.kind === "err" ? (
            <p className="font-semibold text-scan-error">{state.feedback.title}</p>
          ) : (
            <p className="text-sm text-gray-500">Ready to scan</p>
          )}
        </div>
      </div>

      {/* Validate button */}
      <div className="px-4 pb-6">
        <button
          onClick={() => void handleValidate()}
          disabled={state.phase === "validating"}
          className="w-full rounded-xl bg-green-600 py-4 text-lg font-semibold active:bg-green-700 disabled:opacity-50"
        >
          {state.phase === "validating" ? "Validating…" : state.errorMsg ? "Retry Validation" : "Validate Dispatch"}
        </button>
      </div>

      {/* Exception modal */}
      {state.modal && <ExceptionModal modal={state.modal} onDismiss={() => dispatch({ type: "DISMISS" })} />}
    </div>
  );
}

// ─── Exception modal ─────────────────────────────────────────────────────────

interface ExceptionModalProps {
  modal: Modal;
  onDismiss: () => void;
}

function ExceptionModal({ modal, onDismiss }: ExceptionModalProps) {
  let title: string;
  let body: ReactNode;

  if (modal.kind === "unknown") {
    title = "Unknown Barcode";
    body = (
      <>
        <p className="text-gray-300">This barcode was not recognised and has been flagged for review.</p>
        <p className="mt-2 break-all font-mono text-xs text-gray-500">{modal.rawBarcode}</p>
        <p className="mt-1 font-mono text-xs text-gray-600">
          {/* eslint-disable-next-line no-control-regex */}
          {modal.rawBarcode.length} chars · {(modal.rawBarcode.match(/\x1d/g) ?? []).length} GS
        </p>
      </>
    );
  } else if (modal.kind === "not_on_picking") {
    title = "Not on Picking";
    body = (
      <p className="text-gray-300">
        <span className="font-semibold capitalize">{modal.category.replace(/_/g, " ")}</span>{" "}
        batch <span className="font-mono">{modal.batch}</span> is not expected on this picking.
      </p>
    );
  } else if (modal.kind === "already_scanned") {
    title = "Already Scanned";
    body = (
      <p className="text-gray-300">
        Bag <span className="font-mono font-semibold">{modal.lotName}</span> has already been
        scanned on this picking. Skip it and move to the next bag.
      </p>
    );
  } else if (modal.kind === "already_received") {
    title = "Already Dispatched";
    body = (
      <p className="text-gray-300">
        Bag <span className="font-mono font-semibold">{modal.lotName}</span> has already been
        dispatched from stock. Each bag can only be scanned once — check the label and try a
        different bag.
      </p>
    );
  } else if (modal.kind === "variance") {
    title = "Extra Bag";
    body = (
      <div className="space-y-2 text-gray-300">
        <p>
          You&apos;ve scanned <span className="font-semibold">{String(modal.actual)}</span> bags
          but only <span className="font-semibold">{String(modal.expected)}</span> were expected.
        </p>
        <p className="text-sm text-gray-400">
          The extra bag has been recorded. Let your supervisor know if this wasn&apos;t intentional.
        </p>
      </div>
    );
  } else {
    title = modal.title;
    body = <p className="text-sm text-gray-400">{modal.message}</p>;
  }

  return (
    <div className="fixed inset-0 flex items-end justify-center bg-black/70 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6">
        <h3 className="mb-3 text-lg font-bold text-scan-error">{title}</h3>
        <div className="mb-5">{body}</div>
        <button
          onClick={onDismiss}
          className="w-full rounded-xl bg-green-600 py-3 font-semibold active:bg-green-700"
        >
          Continue scanning
        </button>
      </div>
    </div>
  );
}
